import React from "react";
import { Link } from "react-router-dom";
import SEO from "../../../components/SEO";
import img45 from "../../../assets/img45.webp";
import { CheckCircle } from "lucide-react";
import img10 from "../../../assets/img10.jpg";
import BlogSection from "../../../components/BlogSection";

export default function RegisteredRetirementSavingsPlan() {
  const container = "w-full max-w-[1380px] mx-auto";

  return (
    <>
      <SEO
        title="Régime enregistré d'épargne-retraite (REER) - einsured.ca"
        description="Découvrez le REER chez einsured.ca : critères d’admissibilité, cotisations et avantages pour préparer votre retraite et sécuriser votre avenir financier."
        keywords="REER Canada, Régime enregistré d'épargne-retraite, déduction fiscale REER, REER de conjoint, Régime d'accession à la propriété RAP"
      />
      {/* SECTION HÉROS */}
      <section
        className="relative min-h-[60vh] sm:min-h-[70vh] md:h-[80vh] bg-cover bg-center flex items-center justify-center md:justify-start px-4 sm:px-6 md:px-20 text-center md:text-left"
        style={{ backgroundImage: `url(${img45})` }}
      >
        <div className="absolute inset-0 bg-black/50"></div>

        <div className={container}>
          <div className="relative z-10 text-white max-w-3xl">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-3 leading-tight">
              Régimes enregistrés <br /> d'épargne-retraite
            </h1>
            <p className="text-base sm:text-lg font-medium mb-4">
              Épargne-retraite avantageuse sur le plan fiscal qui aide à faire
              croître vos placements et à sécuriser votre avenir avec des
              conseils d'experts
            </p>
            <a
              href="https://book.einsured.ca/widget/bookings/ei-team"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block w-full sm:w-auto bg-sky-400 hover:bg-sky-500 text-white font-semibold px-8 py-3 rounded-full transition shadow-md text-base sm:text-lg"
            >
              Commencer
            </a>
          </div>
        </div>
      </section>

      {/* Régime enregistré d'épargne-retraite */}
      <section className="py-16 sm:py-20 bg-white px-4 sm:px-6 md:px-20 text-gray-800">
        <div className={container}>
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#49628b] text-center mb-8">
              Régime enregistré d'épargne-retraite
            </h2>

            <p className="text-base sm:text-lg leading-7 sm:leading-8 mb-5">
              <a
                href="https://www.canada.ca/fr/agence-revenu/services/impot/particuliers/sujets/reer-et-regimes-connexes/regime-enregistre-epargne-retraite-reer.html"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#20a7df] font-medium underline hover:opacity-90"
              >
                REER signifie Régime enregistré d'épargne-retraite
              </a>
              . Il s'agit d'un compte de placement parrainé par le gouvernement
              canadien, conçu pour aider les particuliers à épargner pour leur
              retraite. Les REER offrent des avantages fiscaux pour encourager
              les Canadiens à épargner pour leurs objectifs financiers à long
              terme, en particulier la retraite.
            </p>

            <p className="text-base sm:text-lg leading-7 sm:leading-8 mb-5">
              Les cotisations au REER sont déductibles d'impôt, ce qui signifie
              que le montant cotisé est soustrait du revenu imposable du
              particulier pour l'année, ce qui peut entraîner une réduction de
              l'impôt sur le revenu à payer. Les placements détenus dans le
              REER peuvent croître en report d'impôt jusqu'à leur retrait.
            </p>

            <p className="text-base sm:text-lg leading-7 sm:leading-8 mb-5">
              Bien que les cotisations procurent une déduction fiscale, les
              retraits d'un REER sont généralement assujettis à l'impôt sur le
              revenu. Toutefois, les particuliers retirent généralement de leur
              REER à la retraite, lorsque leur revenu et leur taux d'imposition
              peuvent être inférieurs à ceux de leurs années de travail.
            </p>

            <p className="text-base sm:text-lg leading-7 sm:leading-8 mb-5">
              Le REER peut détenir une variété de placements, notamment des
              actions, des obligations, des fonds communs de placement et
              d'autres instruments financiers, permettant aux particuliers
              d'adapter leurs portefeuilles à leur tolérance au risque et à
              leurs objectifs financiers. C'est un outil important pour les
              Canadiens afin de constituer un pécule pour la retraite tout en
              bénéficiant d'avantages fiscaux en cours de route.
            </p>

            <p className="text-base sm:text-lg leading-7 sm:leading-8 mb-5">
              Vous avez vu des gens qui n'ont aucune épargne et qui ne peuvent
              compter que sur la SV à leur retraite. De nos jours, alors que les
              revenus stagnent et que les prix augmentent continuellement, de
              nombreux aînés travailleurs sont contraints de vivre modestement.
            </p>

            <p className="text-base sm:text-lg leading-7 sm:leading-8 mb-5">
              Ici chez{" "}
              <a
                href="/"
                className="text-[#20a7df] font-medium underline hover:opacity-90"
              >
                einsured.ca
              </a>{" "}
              nous offrons des Régimes enregistrés d'épargne-retraite qui
              constituent rapidement votre pécule. C'est la façon abordable
              de s'assurer d'avoir l'argent supplémentaire dont vous avez besoin
              pour bien vivre, voyager, passer du temps en famille et poursuivre
              vos intérêts à la retraite.
            </p>

            <p className="text-base sm:text-lg leading-7 sm:leading-8 mb-5">
              <a
                href="/"
                className="text-[#20a7df] font-medium underline hover:opacity-90"
              >
                einsured.ca
              </a>{" "}
              offre ce programme de qualité pour aider les Canadiens à mener une
              vie plus confortable. Vous pouvez être la personne qui a bien
              planifié sa retraite, quelle que soit votre situation.
            </p>

            <p className="text-base sm:text-lg leading-7 sm:leading-8">
              Si vous avez besoin d'aide, vous pouvez{" "}
              <a
                href="https://book.einsured.ca/widget/bookings/ei-team"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#20a7df] font-medium underline hover:opacity-90"
              >
                réserver une consultation gratuite
              </a>{" "}
              avec nous.
            </p>
          </div>
        </div>
      </section>

      {/* Raisons d'acheter un REER */}
      <section
        className="relative py-20 sm:py-24 md:py-28 px-4 sm:px-6 md:px-24 bg-cover bg-center lg:bg-fixed"
        style={{ backgroundImage: `url(${img10})` }}
      >
        <div className="absolute inset-0 bg-sky-900 bg-opacity-75"></div>

        <div className={container}>
          <div className="relative z-10 text-white">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-4">
              Raisons d'acheter un régime enregistré d'épargne-retraite REER
            </h2>

            <p className="text-base sm:text-lg leading-relaxed text-center mb-10">
              Il existe plusieurs raisons convaincantes d'envisager d'acheter un{" "}
              <a
                href="https://www.canada.ca/fr/agence-revenu/services/impot/particuliers/sujets/reer-et-regimes-connexes/regime-enregistre-epargne-retraite-reer.html"
                target="_blank"
                rel="noopener noreferrer"
                className="underline text-[#20a7df]"
              >
                Régime enregistré d'épargne-retraite (REER) au Canada.
              </a>
            </p>

            {/* Grille de cartes */}
            <div className="grid gap-6 sm:gap-8 md:grid-cols-2">
              <div className="bg-white text-[#49628b] rounded-[2.5rem] shadow-lg px-6 sm:px-8 py-8 sm:py-10">
                <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-3">
                  Déductions fiscales :
                </h3>
                <p className="text-base sm:text-lg leading-relaxed text-gray-800">
                  Les cotisations à un REER sont déductibles d'impôt. Cela
                  signifie que l'argent que vous cotisez à votre REER réduit
                  votre revenu imposable pour l'année, ce qui peut entraîner
                  une facture d'impôt sur le revenu moins élevée.
                </p>
              </div>

              <div className="bg-white text-[#49628b] rounded-[2.5rem] shadow-lg px-6 sm:px-8 py-8 sm:py-10">
                <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-3">
                  Croissance à imposition différée :
                </h3>
                <p className="text-base sm:text-lg leading-relaxed text-gray-800">
                  Les placements détenus dans un REER peuvent croître à
                  imposition différée. Vous ne serez pas imposé sur le revenu,
                  les dividendes ou les gains en capital générés par vos
                  placements jusqu'au retrait des fonds du REER.
                </p>
              </div>

              <div className="bg-white text-[#49628b] rounded-[2.5rem] shadow-lg px-6 sm:px-8 py-8 sm:py-10">
                <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-3">
                  Planification du revenu de retraite :
                </h3>
                <p className="text-base sm:text-lg leading-relaxed text-gray-800">
                  Les REER sont conçus pour aider les Canadiens à épargner pour
                  la retraite. En cotisant régulièrement au cours de vos années
                  de travail, vous pouvez constituer un pécule important pour
                  fournir un revenu pendant vos années de retraite.
                </p>
              </div>

              <div className="bg-white text-[#49628b] rounded-[2.5rem] shadow-lg px-6 sm:px-8 py-8 sm:py-10">
                <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-3">
                  Cotisations de l'employeur :
                </h3>
                <p className="text-base sm:text-lg leading-relaxed text-gray-800">
                  De nombreux employeurs offrent des régimes d'épargne-retraite
                  en milieu de travail, comme un REER collectif, où ils peuvent
                  égaler une partie de vos cotisations. Profiter des cotisations
                  de l'employeur est un moyen d'augmenter votre épargne-retraite.
                </p>
              </div>

              <div className="bg-white text-[#49628b] rounded-[2.5rem] shadow-lg px-6 sm:px-8 py-8 sm:py-10">
                <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-3">
                  Régime d'accession à la propriété (RAP) :
                </h3>
                <p className="text-base sm:text-lg leading-relaxed text-gray-800">
                  Le{" "}
                  <a
                    href="https://www.canada.ca/fr/agence-revenu/services/impot/particuliers/sujets/reer-et-regimes-connexes/qu-est-ce-que-le-regime-accession-propriete.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline text-[#20a7df]"
                  >
                    RAP
                  </a>{" "}
                  vous permet de retirer jusqu'à un certain montant de votre REER
                  pour acheter ou construire une habitation admissible sans
                  entraîner d'impôts immédiats. Cela peut être une stratégie utile
                  pour les acheteurs d'une première maison.
                </p>
              </div>

              <div className="bg-white text-[#49628b] rounded-[2.5rem] shadow-lg px-6 sm:px-8 py-8 sm:py-10">
                <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-3">
                  Régime d'éducation permanente (REP) :
                </h3>
                <p className="text-base sm:text-lg leading-relaxed text-gray-800">
                  Le REP vous permet de retirer des fonds de votre REER pour
                  financer une formation ou des études à temps plein pour vous
                  ou votre conjoint sans conséquences fiscales immédiates.
                </p>
              </div>

              <div className="bg-white text-[#49628b] rounded-[2.5rem] shadow-lg px-6 sm:px-8 py-8 sm:py-10">
                <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-3">
                  Fractionnement du revenu à la retraite :
                </h3>
                <p className="text-base sm:text-lg leading-relaxed text-gray-800">
                  À la retraite, vous avez la possibilité de fractionner votre
                  revenu REER avec votre conjoint ou conjoint de fait. Cela peut
                  entraîner des économies d'impôt potentielles, surtout s'il y a
                  une différence significative dans vos niveaux de revenus.
                </p>
              </div>

              <div className="bg-white text-[#49628b] rounded-[2.5rem] shadow-lg px-6 sm:px-8 py-8 sm:py-10">
                <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-3">
                  Protection contre les créanciers :
                </h3>
                <p className="text-base sm:text-lg leading-relaxed text-gray-800">
                  Les REER peuvent offrir un certain niveau de protection contre
                  les créanciers dans certaines situations, fournissant une
                  couche supplémentaire de sécurité financière.
                </p>
              </div>
            </div>

            <div className="mt-12 max-w-5xl mx-auto text-base sm:text-lg leading-relaxed">
              <p className="mb-6">
                Il est important de noter que bien que les REER offrent des
                avantages fiscaux, ils ont également des limites de cotisation et
                des retraits obligatoires éventuels par le biais du{" "}
                <a
                  href="https://www.canada.ca/fr/agence-revenu/services/impot/particuliers/sujets/fonds-enregistre-revenu-retraite-ferr.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#20a7df] underline"
                >
                  Fonds enregistré de revenu de retraite (FERR) à la retraite.
                </a>
              </p>

              <p className="mb-10">
                Comme les situations financières individuelles varient, il est
                conseillé de consulter un conseiller financier chez{" "}
                <a href="/" className="text-[#20a7df] underline">
                  einsured.ca
                </a>{" "}
                pour déterminer la meilleure approche en fonction de vos besoins
                et objectifs spécifiques.
              </p>
              {/* Appel à l'action */}
              <div className="flex justify-center">
                <a
                  href="https://book.einsured.ca/widget/bookings/ei-team"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-10 py-3 rounded-full bg-[#00b4ff] hover:bg-[#00a3e6] text-[#05203c] font-semibold text-lg shadow-md transition w-full sm:w-auto"
                >
                  Commencer
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Types de REER */}
      <section className="py-16 sm:py-20 bg-white px-4 sm:px-6 md:px-20 text-gray-800">
        <div className={container}>
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#49628b] text-center mb-8">
              Quels sont les différents types de REER :
            </h2>

            <p className="text-base sm:text-lg leading-7 sm:leading-8 mb-4">
              Il existe différents types de{" "}
              <span className="font-semibold text-[#49628b]">
                Régimes enregistrés d'épargne-retraite (REER)
              </span>{" "}
              au Canada, chacun conçu pour répondre à divers besoins et objectifs
              financiers.
            </p>

            <p className="text-[#49628b] text-base sm:text-lg leading-7 sm:leading-8 font-semibold mb-6">
              Voici quelques types courants de REER :
            </p>

            <div className="space-y-4 sm:space-y-5">
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-sky-500 mt-1 flex-shrink-0" />
                <p className="text-base sm:text-lg leading-relaxed">
                  <span className="font-semibold text-[#49628b]">
                    REER individuel :
                  </span>{" "}
                  Il s'agit du type de REER le plus courant. Les particuliers
                  cotisent à leur propre REER, et les cotisations sont admissibles
                  aux déductions fiscales.
                </p>
              </div>

              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-sky-500 mt-1 flex-shrink-0" />
                <p className="text-base sm:text-lg leading-relaxed">
                  <span className="font-semibold text-[#49628b]">
                    REER de conjoint :
                  </span>{" "}
                  Un REER de conjoint permet aux personnes ayant un revenu plus
                  élevé de cotiser à un REER au nom de leur conjoint. Cette
                  stratégie peut aider à égaliser le revenu de retraite et
                  potentiellement réduire les impôts globaux à la retraite.
                </p>
              </div>

              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-sky-500 mt-1 flex-shrink-0" />
                <p className="text-base sm:text-lg leading-relaxed">
                  <span className="font-semibold text-[#49628b]">
                    REER collectif :
                  </span>{" "}
                  Offert par les employeurs, un REER collectif est un régime
                  d'épargne-retraite en milieu de travail. Les employés cotisent
                  à leur REER par le biais de retenues sur la paie, et certains
                  employeurs peuvent offrir des cotisations correspondantes.
                </p>
              </div>

              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-sky-500 mt-1 flex-shrink-0" />
                <p className="text-base sm:text-lg leading-relaxed">
                  <span className="font-semibold text-[#49628b]">
                    REER immobilisé (CRI) :
                  </span>{" "}
                  Les REER immobilisés sont créés lorsque des fonds d'un régime
                  de retraite sont transférés à un REER, offrant plus de contrôle
                  sur les placements. Cependant, il existe des restrictions sur
                  les retraits en raison des fonds « immobilisés » jusqu'à la retraite.
                </p>
              </div>

              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-sky-500 mt-1 flex-shrink-0" />
                <p className="text-base sm:text-lg leading-relaxed">
                  <span className="font-semibold text-[#49628b]">
                    REER groupé :
                  </span>{" "}
                  Les REER groupés regroupent les cotisations de plusieurs
                  particuliers et les investissent dans un fonds géré
                  professionnellement. Cette approche fournit un portefeuille
                  de placements diversifié et est souvent offerte par des
                  institutions financières ou des sociétés de placement.
                </p>
              </div>

              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-sky-500 mt-1 flex-shrink-0" />
                <p className="text-base sm:text-lg leading-relaxed">
                  <span className="font-semibold text-[#49628b]">
                    REER autogéré :
                  </span>{" "}
                  Dans un REER autogéré, les particuliers ont un plus grand
                  contrôle sur leurs placements et peuvent choisir parmi une
                  large gamme d'options de placement, notamment des actions
                  individuelles, des obligations et d'autres titres.
                </p>
              </div>

              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-sky-500 mt-1 flex-shrink-0" />
                <p className="text-base sm:text-lg leading-relaxed">
                  <span className="font-semibold text-[#49628b]">
                    Régime d'épargne restreint (RER) :
                  </span>{" "}
                  Ce type de régime est souvent utilisé par les travailleurs
                  autonomes ou ceux ayant un revenu irrégulier. Il permet des
                  calendriers de cotisation plus flexibles.
                </p>
              </div>

              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-sky-500 mt-1 flex-shrink-0" />
                <p className="text-base sm:text-lg leading-relaxed">
                  <span className="font-semibold text-[#49628b]">
                    REER à prestations variables :
                  </span>{" "}
                  Ce type de REER est conçu pour fournir un revenu de retraite
                  variable. Il est couramment utilisé lors de la conversion d'un
                  REER en{" "}
                  <a
                    href="https://www.canada.ca/fr/agence-revenu/services/impot/particuliers/sujets/fonds-enregistre-revenu-retraite-ferr.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#20a7df] underline"
                  >
                    Fonds enregistré de revenu de retraite (FERR)
                  </a>{" "}
                  pour recevoir des paiements périodiques à la retraite.
                </p>
              </div>

              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-sky-500 mt-1 flex-shrink-0" />
                <p className="text-base sm:text-lg leading-relaxed">
                  <span className="font-semibold text-[#49628b]">
                    REER de rente :
                  </span>{" "}
                  Un REER de rente permet aux particuliers de convertir leur
                  épargne REER en un flux de paiements de revenu réguliers,
                  fournissant un revenu garanti pendant la retraite.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Admissibilité au REER */}
      <section className="py-16 sm:py-2 bg-white px-4 sm:px-6 md:px-20 text-gray-800">
        <div className={container}>
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#49628b] text-center mb-8">
              Admissibilité au REER :
            </h2>

            <p className="text-base sm:text-lg leading-7 sm:leading-8 mb-4">
              Pour être admissible à cotiser à un{" "}
              <span className="font-semibold text-[#49628b]">
                Régime enregistré d'épargne-retraite (REER)
              </span>{" "}
              au Canada, les particuliers doivent satisfaire à certains critères.
            </p>

            <p className="text-base sm:text-lg leading-7 sm:leading-8 mb-6">
              Voici les{" "}
              <a
                href="https://www.canada.ca/fr/agence-revenu/services/impot/particuliers/sujets/reer-et-regimes-connexes/etablir-un-reer.html"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#20a7df] underline"
              >
                principales conditions d'admissibilité :
              </a>
            </p>

            <div className="space-y-4 sm:space-y-5">
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-sky-500 mt-1 flex-shrink-0" />
                <p className="text-base sm:text-lg leading-relaxed">
                  <span className="font-semibold text-[#49628b]">Âge :</span>{" "}
                  Les particuliers doivent avoir 18 ans ou plus pour ouvrir un
                  REER et y cotiser. Il n'y a pas d'âge maximum pour cotiser à
                  un REER, mais il est obligatoire de convertir le REER en un
                  Fonds enregistré de revenu de retraite (FERR) ou d'acheter
                  une rente avant la fin de l'année au cours de laquelle le
                  titulaire du compte atteint l'âge de 71 ans.
                </p>
              </div>

              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-sky-500 mt-1 flex-shrink-0" />
                <p className="text-base sm:text-lg leading-relaxed">
                  <span className="font-semibold text-[#49628b]">
                    Revenu gagné :
                  </span>{" "}
                  Les particuliers doivent avoir un revenu gagné, qui comprend
                  le revenu d'emploi, le revenu d'entreprise, le revenu de
                  location et d'autres types de revenu admissibles. Le revenu
                  de placement, le revenu de pension et d'autres formes de
                  revenu non gagné ne comptent pas comme revenu admissible pour
                  les cotisations REER.
                </p>
              </div>

              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-sky-500 mt-1 flex-shrink-0" />
                <p className="text-base sm:text-lg leading-relaxed">
                  <span className="font-semibold text-[#49628b]">
                    Résidence au Canada :
                  </span>{" "}
                  Pour cotiser à un REER, les particuliers doivent être
                  résidents du Canada. Les non-résidents ne sont pas admissibles
                  à cotiser à un REER, bien qu'ils puissent avoir des options
                  pour transférer des fonds d'un REER existant.
                </p>
              </div>

              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-sky-500 mt-1 flex-shrink-0" />
                <p className="text-base sm:text-lg leading-relaxed">
                  <span className="font-semibold text-[#49628b]">
                    Droits de cotisation :
                  </span>{" "}
                  <a
                    href="https://www.canada.ca/fr/agence-revenu/services/impot/particuliers/sujets/reer-et-regimes-connexes/cotiser-a-un-reer-rpac/ou-trouver-votre-plafond-de-deduction-reer-rpac.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#20a7df] underline"
                  >
                    Les droits de cotisation
                  </a>{" "}
                  sont déterminés en fonction du revenu gagné d'un particulier
                  et sont soumis à une limite annuelle fixée par l'Agence du
                  revenu du Canada (ARC). Les droits de cotisation inutilisés
                  peuvent être reportés aux années futures.
                </p>
              </div>

              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-sky-500 mt-1 flex-shrink-0" />
                <p className="text-base sm:text-lg leading-relaxed">
                  <span className="font-semibold text-[#49628b]">
                    Statut de déclaration fiscale :
                  </span>{" "}
                  Pour cotiser à un REER, les particuliers doivent avoir produit
                  une déclaration de revenus au Canada. Ceci est important pour
                  suivre les droits de cotisation et s'assurer que les
                  cotisations sont admissibles aux déductions fiscales.
                </p>
              </div>

              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-sky-500 mt-1 flex-shrink-0" />
                <p className="text-base sm:text-lg leading-relaxed">
                  <span className="font-semibold text-[#49628b]">
                    Facteur d'équivalence (FE) :
                  </span>{" "}
                  Les particuliers ayant des régimes de pension peuvent avoir un
                  Facteur d'équivalence (FE) qui réduit leurs droits de
                  cotisation au REER. Le FE est fourni par les employeurs et
                  représente la valeur des prestations de pension accumulées
                  au cours de l'année.
                </p>
              </div>
            </div>

            <div className="mt-10 flex justify-center">
              <a
                href="https://book.einsured.ca/widget/bookings/ei-team"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-10 py-3 rounded-full bg-[#00b4ff] hover:bg-[#00a3e6] text-[#05203c] font-semibold text-lg shadow-md transition w-full sm:w-auto"
              >
                Investir maintenant
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Comment ouvrir un compte REER */}
      <section className="bg-[#dce2ec] border-y-4 border-[#2b4672] py-16 sm:py-16 px-4 sm:px-6 md:px-20 text-gray-800 mt-10">
        <div className={container}>
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#49628b] text-center mb-8">
              Comment ouvrir un compte REER ?
            </h2>

            <p className="text-base sm:text-lg leading-7 sm:leading-8 mb-10 text-left">
              L'ouverture d'un compte{" "}
              <span className="font-semibold text-[#49628b]">
                Régime enregistré d'épargne-retraite (REER)
              </span>{" "}
              au Canada comprend plusieurs étapes. Voici un guide général sur
              la façon d'ouvrir un compte REER :
            </p>

            {/* Grille de cartes */}
            <div className="grid gap-6 sm:gap-8 md:grid-cols-2">
              <div className="bg-white rounded-[2.5rem] shadow-md px-6 sm:px-8 py-8 sm:py-10">
                <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] text-center mb-4">
                  Choisir une institution financière :
                </h3>
                <p className="text-base sm:text-lg leading-relaxed">
                  Décidez où vous souhaitez ouvrir votre compte REER. Il peut
                  s'agir d'une banque, d'une caisse, d'une société de placement
                  ou d'autres institutions financières.
                </p>
              </div>

              <div className="bg-white rounded-[2.5rem] shadow-md px-6 sm:px-8 py-8 sm:py-10">
                <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] text-center mb-4">
                  Rassembler les documents requis :
                </h3>
                <p className="text-base sm:text-lg leading-relaxed">
                  Assurez-vous d'avoir les documents d'identification
                  nécessaires, comme une pièce d'identité avec photo délivrée
                  par le gouvernement (permis de conduire, passeport) et votre
                  numéro d'assurance sociale (NAS). Certaines institutions
                  peuvent également exiger une preuve d'adresse.
                </p>
              </div>

              <div className="bg-white rounded-[2.5rem] shadow-md px-6 sm:px-8 py-8 sm:py-10">
                <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] text-center mb-4">
                  Déterminer vos droits de cotisation :
                </h3>
                <p className="text-base sm:text-lg leading-relaxed">
                  Vérifiez vos droits de cotisation au REER, qui sont indiqués
                  sur votre Avis de cotisation de l'Agence du revenu du Canada
                  (ARC) après avoir produit votre déclaration de revenus. Les
                  droits de cotisation sont également disponibles dans votre
                  compte Mon dossier de l'ARC.
                </p>
              </div>

              <div className="bg-white rounded-[2.5rem] shadow-md px-6 sm:px-8 py-8 sm:py-10">
                <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] text-center mb-4">
                  Choisir le type de REER :
                </h3>
                <p className="text-base sm:text-lg leading-relaxed">
                  Décidez du type de compte REER que vous souhaitez ouvrir.
                  Il pourrait s'agir d'un REER individuel, d'un REER de conjoint
                  ou d'autres types spécialisés selon vos besoins et objectifs
                  financiers.
                </p>
              </div>

              <div className="bg-white rounded-[2.5rem] shadow-md px-6 sm:px-8 py-8 sm:py-10">
                <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] text-center mb-4">
                  Choisir les placements :
                </h3>
                <p className="text-base sm:text-lg leading-relaxed">
                  Déterminez comment vous souhaitez investir vos cotisations
                  REER. Les institutions financières offrent diverses options
                  de placement, notamment des fonds communs de placement, des
                  CPG, des actions, des obligations et des FNB. Certains
                  particuliers peuvent opter pour un REER autogéré pour plus
                  de contrôle sur leurs placements.
                </p>
              </div>

              <div className="bg-white rounded-[2.5rem] shadow-md px-6 sm:px-8 py-8 sm:py-10">
                <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] text-center mb-4">
                  Remplir les formulaires de demande :
                </h3>
                <p className="text-base sm:text-lg leading-relaxed">
                  Remplissez les formulaires de demande requis fournis par
                  l'institution financière choisie. Cela peut inclure des
                  informations sur vos coordonnées personnelles, vos préférences
                  de placement et les désignations de bénéficiaires.
                </p>
              </div>

              <div className="bg-white rounded-[2.5rem] shadow-md px-6 sm:px-8 py-8 sm:py-10">
                <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] text-center mb-4">
                  Déposer des fonds :
                </h3>
                <p className="text-base sm:text-lg leading-relaxed">
                  Déposez des fonds dans votre compte REER. Vous pouvez
                  effectuer des cotisations forfaitaires ou mettre en place des
                  cotisations régulières selon votre capacité financière.
                </p>
              </div>

              <div className="bg-white rounded-[2.5rem] shadow-md px-6 sm:px-8 py-8 sm:py-10">
                <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] text-center mb-4">
                  Examiner et confirmer :
                </h3>
                <p className="text-base sm:text-lg leading-relaxed">
                  Examinez les modalités de la convention de compte REER.
                  Assurez-vous de comprendre les frais, les règles de retrait
                  et toute autre condition. Une fois satisfait, confirmez votre
                  demande.
                </p>
              </div>

              <div className="bg-white rounded-[2.5rem] shadow-md px-6 sm:px-8 py-8 sm:py-10">
                <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] text-center mb-4">
                  Surveiller vos placements :
                </h3>
                <p className="text-base sm:text-lg leading-relaxed">
                  Surveillez régulièrement le rendement de vos placements dans
                  le compte REER. Envisagez de revoir périodiquement votre
                  stratégie de placement pour l'aligner sur vos objectifs
                  financiers.
                </p>
              </div>

              <div className="bg-white rounded-[2.5rem] shadow-md px-6 sm:px-8 py-8 sm:py-10">
                <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] text-center mb-4">
                  Produire votre déclaration de revenus :
                </h3>
                <p className="text-base sm:text-lg leading-relaxed">
                  Déclarez vos cotisations REER dans votre déclaration de
                  revenus annuelle pour bénéficier des déductions fiscales
                  potentielles et assurer un suivi précis de vos droits de
                  cotisation.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      <BlogSection title="Derniers articles" mode="related" relatedCount={3} />
    </>
  );
}
